import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    useTheme,
} from '@mui/material';

import { useTranslation } from 'react-i18next';
import ContentArea from '../../components/contentArea';
import Button from '../../components/button';
import { SortableHeaderCell, HeaderCell } from '../../components/table/headerCell';
import { getTableStyles } from '../../components/table/styles';
import { formatDateTimeDisplay, createSortConfig, handleSort, formatTime, isAdmin } from '../../utils/base';
import { didimAPI, organizationAPI, membershipAPI } from '../../actions/imsServer';
import { Prev, Next } from '../../assets/images';
import { API_PAGE_SIZE } from '../../constants';

const DidimList = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const theme = useTheme();

    const [didims, setDidims] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [sortConfig, setSortConfig] = useState(createSortConfig());
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const ITEMS_PER_PAGE = API_PAGE_SIZE;

    // meInfo에서 등급 확인
    const meInfo = JSON.parse(localStorage.getItem('meInfo') || '{}');

    const fetchData = async (page = 1, currentSortConfig = sortConfig) => {
        try {
            setLoading(true);
            setError(null);

            const currentOffset = (page - 1) * ITEMS_PER_PAGE;

            // grade가 50~60이면 사용자의 기관 목록 가져오기
            let userOrgId = null;
            if (isAdmin(meInfo?.grade) && meInfo?.id) {
                try {
                    // grade 50, 60 모두 membership에서 가져오기
                    const membershipsResponse = await membershipAPI.getMemberships({ userId: meInfo.id, type: 0, state: 0, expand: 'organization' });
                    const memberships = membershipsResponse?.data?.memberships || [];
                    userOrgId = memberships.length > 0 ? memberships[0].organizationId : null;

                    if (!userOrgId) {
                        console.log('Admin user but no organization found');
                        setDidims([]);
                        setLoading(false);
                        setTotalCount(0);
                        return;
                    }
                } catch (err) {
                    console.error('Failed to fetch user organizations:', err);
                    if (isAdmin(meInfo?.grade)) {
                        setDidims([]);
                        setLoading(false);
                        setTotalCount(0);
                        return;
                    }
                }
            }

            // 어드민이면 해당 기관 ID로 디딤 조회, 아니면 전체 조회
            const didimParams = {
                state: 0,
                expand: 'organization,playInfo.todayPlayTime',
                offset: currentOffset,
                limit: ITEMS_PER_PAGE
            };

            // 서버사이드 정렬 파라미터 추가
            if (currentSortConfig.key) {
                didimParams.sort = currentSortConfig.key;
                didimParams.order = currentSortConfig.direction === 'asc' ? 'asc' : 'desc';
            }

            if (isAdmin(meInfo?.grade) && userOrgId) {
                didimParams.organizationId = userOrgId;
            } else if (isAdmin(meInfo?.grade) && !userOrgId) {
                // 어드민인데 기관 ID가 없으면 빈 목록 표시
                setDidims([]);
                setLoading(false);
                setTotalCount(0);
                return;
            }

            const didimRes = await didimAPI.getDidims(didimParams);

            let didimList = didimRes?.data?.didims || didimRes?.didims || [];
            const total = didimRes?.data?.total || didimRes?.total || didimList.length;

            const rows = didimList.map(d => {
                // expand: 'organization,playInfo.todayPlayTime'으로 organization과 playInfo 정보가 포함되어 있음
                const org = d.organization || {};
                // playInfo.todayPlayTime에서 금일 사용 시간 가져오기 (분 단위)
                const dailyUsageMin = d.playInfo?.todayPlayTime ? Math.round(d.playInfo.todayPlayTime) : 0;

                return {
                    id: d.id,
                    name: d.name || '-',
                    uid: d.uid || '-',
                    createDate: d.createDate || null,
                    organizationId: d.organizationId || 0,
                    organizationName: org?.name || '-',
                    country: org?.country || '-',
                    info: d.info || '',
                    playingStartTime: d.playingStartTime || null,
                    dailyUsage: dailyUsageMin,
                };
            });

            setDidims(rows);
            setTotalCount(total);
        } catch (err) {
            console.error('Failed to fetch didims:', err);
            setError(t('failed_to_load_didim_list'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setCurrentPage(1);
        setDidims([]);
        fetchData(1, sortConfig);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchData는 매 렌더 새 참조라 의존성에서 제외
    }, []);

    // 페이지 변경 핸들러
    const handlePageChange = useCallback((newPage) => {
        if (newPage < 1) return;
        const maxPage = Math.ceil(totalCount / ITEMS_PER_PAGE);
        if (newPage > maxPage && maxPage > 0) return;

        setCurrentPage(newPage);
        fetchData(newPage, sortConfig);
    }, [totalCount, sortConfig]);

    // 이전 페이지
    const handlePrevPage = useCallback(() => {
        if (currentPage > 1) {
            handlePageChange(currentPage - 1);
        }
    }, [currentPage, handlePageChange]);

    // 다음 페이지
    const handleNextPage = useCallback(() => {
        const maxPage = Math.ceil(totalCount / ITEMS_PER_PAGE);
        if (currentPage < maxPage) {
            handlePageChange(currentPage + 1);
        }
    }, [currentPage, totalCount, handlePageChange]);

    const onSort = (key) => {
        const next = handleSort(sortConfig, key);
        setSortConfig(next);
        // 정렬 시 첫 페이지로 리셋
        setCurrentPage(1);
        setDidims([]);
        fetchData(1, next);
    };

    const handleEdit = (row) => {
        navigate('/didims/edit', { state: { didim: row } });
    };

    return (
        <ContentArea sx={{
            padding: '20px 0px 0px 0px',
            '&::-webkit-scrollbar': {
                width: '8px',
            },
            '&::-webkit-scrollbar-track': {
                backgroundColor: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
                backgroundColor: theme.palette.figma?.white?.[20] || 'rgba(0, 0, 0, 0.2)',
                borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
                backgroundColor: theme.palette.figma?.white?.[40] || 'rgba(0, 0, 0, 0.3)',
            },
        }}>
            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <Typography>{t('loading')}</Typography>
                </Box>
            )}

            {error && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <Typography color="error">{error}</Typography>
                </Box>
            )}

            {!loading && !error && (
                <Box sx={{ px: 2.5, mb: 3 }}>
                    <TableContainer
                        component={Paper}
                    >
                        <Table aria-label="didim table">
                            <TableHead>
                                <TableRow>
                                    <SortableHeaderCell
                                        columnKey="id"
                                        label="ID"
                                        sortConfig={sortConfig}
                                        onSort={onSort}
                                        align="center"
                                    />
                                    <SortableHeaderCell
                                        columnKey="name"
                                        label={t('model_name')}
                                        sortConfig={sortConfig}
                                        onSort={onSort}
                                        align="center"
                                    />
                                    <SortableHeaderCell
                                        columnKey="uid"
                                        label={t('serial')}
                                        sortConfig={sortConfig}
                                        onSort={onSort}
                                        align="center"
                                    />
                                    <SortableHeaderCell
                                        columnKey="createDate"
                                        label={t('product_registration_date')}
                                        sortConfig={sortConfig}
                                        onSort={onSort}
                                        align="center"
                                    />
                                    <SortableHeaderCell
                                        columnKey="organizationId"
                                        label={t('organization_name')}
                                        sortConfig={sortConfig}
                                        onSort={onSort}
                                        align="center"
                                    />
                                    <HeaderCell
                                        label={t('country')}
                                        align="center"
                                    />
                                    <SortableHeaderCell
                                        columnKey="info"
                                        label={t('remarks')}
                                        sortConfig={sortConfig}
                                        onSort={onSort}
                                        align="center"
                                    />
                                    <SortableHeaderCell
                                        columnKey="playingStartTime"
                                        label={t('organization_registration_date')}
                                        sortConfig={sortConfig}
                                        onSort={onSort}
                                        align="center"
                                    />
                                    <HeaderCell
                                        label={t('today_usage_time')}
                                        align="center"
                                    />
                                    <HeaderCell label={t('edit')} align="center" />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {Array.isArray(didims) && didims.length > 0 ? didims.map((row) => (
                                    <TableRow key={row.id} sx={{
                                        cursor: 'pointer',
                                        ...getTableStyles(theme).tableRowStyle
                                    }}>
                                        <TableCell sx={{ textAlign: 'right' }}>{row.id}</TableCell>
                                        <TableCell>{row.name}</TableCell>
                                        <TableCell>{row.uid}</TableCell>
                                        <TableCell>{formatDateTimeDisplay(row.createDate, 'date') || '-'}</TableCell>
                                        <TableCell>{row.organizationName}</TableCell>
                                        <TableCell sx={{ textAlign: 'center' }}>{row.country === 'ko' ? t('south_korea') : row.country}</TableCell>
                                        <TableCell>{row.info}</TableCell>
                                        <TableCell>{formatDateTimeDisplay(row.playingStartTime, 'date') || '-'}</TableCell>
                                        <TableCell sx={{ textAlign: 'center' }}>{formatTime(row.dailyUsage, t) || ''}</TableCell>
                                        <TableCell>
                                            <Button
                                                variant="normal"
                                                size="small"
                                                onClick={() => handleEdit(row)}
                                            >
                                                {t('edit')}
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                )) : (
                                    <TableRow>
                                        <TableCell colSpan={10} sx={{ textAlign: 'center', py: 4 }}>
                                            {loading ? t('loading') : t('no_didim_data')}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* 페이징 컨트롤 */}
                    {totalCount > 0 && (
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: '10px',
                            padding: '20px 0',
                        }}>
                            <Box
                                onClick={handlePrevPage}
                                sx={{
                                    width: '20px',
                                    height: '20px',
                                    cursor: currentPage > 1 ? 'pointer' : 'default',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    opacity: currentPage > 1 ? 1 : 0.3,
                                }}
                            >
                                <Prev color={theme.palette.figma?.white?.[80] || 'rgba(255, 255, 255, 0.8)'} size={20} />
                            </Box>
                            <Typography sx={{
                                fontSize: '16px',
                                fontWeight: 500,
                                color: theme.palette.figma?.white?.[80] || 'rgba(255, 255, 255, 0.8)',
                                letterSpacing: '-0.48px',
                                whiteSpace: 'nowrap'
                            }}>
                                {currentPage} / {Math.ceil(totalCount / ITEMS_PER_PAGE)}
                            </Typography>
                            <Box
                                onClick={handleNextPage}
                                sx={{
                                    width: '20px',
                                    height: '20px',
                                    cursor: currentPage < Math.ceil(totalCount / ITEMS_PER_PAGE) ? 'pointer' : 'default',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    opacity: currentPage < Math.ceil(totalCount / ITEMS_PER_PAGE) ? 1 : 0.3,
                                }}
                            >
                                <Next color={theme.palette.figma?.white?.[80] || 'rgba(255, 255, 255, 0.8)'} size={20} />
                            </Box>
                        </Box>
                    )}
                </Box>
            )}
        </ContentArea>
    );
};

export default DidimList;


